import { useState, useEffect } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import toast from 'react-hot-toast';
import { WatchlistPlayerData } from './types';
import { getWatchlistPlayers, removeFromWatchlist } from '../../lib/supabase';
import { useUserStore } from '../../lib/userStore';
import { useLeagueStore } from '../../lib/leagueStore';
import { translate } from '../../lib/translations';

interface WatchlistSectionProps {
  language: 'en' | 'fr';
  onRefresh: () => void;
  isStandardFlexible: boolean;
}

export function WatchlistSection({ language, onRefresh, isStandardFlexible }: WatchlistSectionProps) {
  const [watchlistPlayers, setWatchlistPlayers] = useState<WatchlistPlayerData[]>([]);
  const [loading, setLoading] = useState(true);
  const [removingPlayer, setRemovingPlayer] = useState<number | null>(null);
  
  const currentUser = useUserStore((state) => state.user_id);
  const selectedLeague = useLeagueStore((state) => state.selectedLeague);

  // Charger la liste de surveillance
  const loadWatchlist = async () => {
    if (!selectedLeague || !currentUser) return;
    
    setLoading(true);
    try {
      const { data, error } = await getWatchlistPlayers(selectedLeague.id, Number(currentUser));
      if (error) throw error;
      setWatchlistPlayers(data);
    } catch (error) {
      console.error('Error loading watchlist:', error);
      toast.error(language === 'fr' 
        ? 'Erreur lors du chargement de la liste de surveillance'
        : 'Error loading watchlist');
    } finally {
      setLoading(false);
    }
  };

  // Retirer un joueur de la liste de surveillance
  const handleRemoveFromWatchlist = async (playerId: number, playerName: string) => {
    if (!selectedLeague || !currentUser) return;
    
    setRemovingPlayer(playerId);
    try {
      const { success, error } = await removeFromWatchlist(selectedLeague.id, Number(currentUser), playerId);
      
      if (success) {
        setWatchlistPlayers(prev => prev.filter(p => p.player_id !== playerId));
        toast.success(language === 'fr'
          ? `${playerName} retiré de votre liste de surveillance`
          : `${playerName} removed from your watchlist`);
        onRefresh(); // Rafraîchir les listes d'agents libres
      } else {
        throw new Error(error?.message || 'Erreur inconnue');
      }
    } catch (error: any) {
      console.error('Error removing from watchlist:', error);
      toast.error(error.message || (language === 'fr'
        ? 'Erreur lors du retrait de la liste de surveillance'
        : 'Error removing from watchlist'));
    } finally {
      setRemovingPlayer(null);
    }
  };

  useEffect(() => {
    loadWatchlist();
  }, [selectedLeague, currentUser]);

  if (loading) {
    return (
      <div className="text-center p-4">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">
            {language === 'fr' ? 'Chargement...' : 'Loading...'}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header bg-primary text-white">
        <h5 className="card-title mb-0 d-flex align-items-center">
          <Eye size={20} className="me-2" />
          {language === 'fr' ? 'Joueurs Surveillés' : 'Watched Players'}
          <span className="badge bg-secondary ms-2">{watchlistPlayers.length}</span>
        </h5>
      </div>
      
      <div className="card-body p-0">
        {watchlistPlayers.length === 0 ? (
          <div className="text-center py-4">
            <Eye size={48} className="text-muted mb-3" />
            <h6 className="text-muted">
              {language === 'fr' 
                ? 'Aucun joueur surveillé' 
                : 'No players being watched'}
            </h6>
            <p className="text-muted small">
              {language === 'fr'
                ? 'Cliquez sur l\'icône + dans la liste des agents libres pour surveiller un joueur'
                : 'Click the + icon in the free agents list to watch a player'}
            </p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead className="table-light">
                <tr>
                  <th className="px-3 py-2">
                    {language === 'fr' ? 'Joueur' : 'Player'}
                  </th>
                  <th className="px-3 py-2 text-center">
                    {translate('position', language)}
                  </th>
                  <th className="px-3 py-2 text-center">
                    {translate('team', language)}
                  </th>
                  <th className="px-3 py-2 text-center">
                    {translate('salary', language)}
                  </th>
                  {!isStandardFlexible && (
                    <th className="px-3 py-2 text-center">
                      {language === 'fr' ? 'Propriétaire' : 'Owner'}
                    </th>
                  )}
                  <th className="px-3 py-2 text-center">
                    {language === 'fr' ? 'Ajouté le' : 'Added on'}
                  </th>
                  <th className="px-3 py-2 text-center">
                    {language === 'fr' ? 'Actions' : 'Actions'}
                  </th>
                </tr>
              </thead>
              <tbody>
                {watchlistPlayers.map((player) => (
                  <tr key={player.id} className="align-middle">
                    <td className="px-3 py-2">
                      <div className="d-flex align-items-center">
                        <div>
                          <div className="fw-semibold">{player.player_name}</div>
                          {player.is_goalie && (
                            <small className="text-muted">
                              {language === 'fr' ? 'Gardien' : 'Goalie'}
                            </small>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span className={`badge ${
                        player.is_goalie ? 'bg-info' : 
                        player.player_position === 'D' ? 'bg-success' : 'bg-primary'
                      }`}>
                        {player.player_position}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span className="badge bg-secondary">
                        {player.team_abbrev || 'N/A'}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-center">
                      {player.salary ? (
                        <span className="text-success fw-semibold">
                          ${player.salary?.toLocaleString()}
                        </span>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    {!isStandardFlexible && (
                      <td className="px-3 py-2 text-center">
                        {player.is_free_agent ? (
                          <span className="badge bg-success">
                            {language === 'fr' ? 'Agent libre' : 'Free Agent'}
                          </span>
                        ) : (
                          <span className="badge bg-warning text-dark">
                            {player.owner_name || 'Propriétaire inconnu'}
                          </span>
                        )}
                      </td>
                    )}
                    <td className="px-3 py-2 text-center">
                      <small className="text-muted">
                        {new Date(player.created_at).toLocaleDateString(
                          language === 'fr' ? 'fr-CA' : 'en-CA'
                        )}
                      </small>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleRemoveFromWatchlist(player.player_id, player.player_name)}
                        disabled={removingPlayer === player.player_id}
                        title={language === 'fr' ? 'Retirer de la surveillance' : 'Remove from watchlist'}
                      >
                        {removingPlayer === player.player_id ? (
                          <span className="spinner-border spinner-border-sm" role="status">
                            <span className="visually-hidden">
                              {language === 'fr' ? 'Chargement...' : 'Loading...'}
                            </span>
                          </span>
                        ) : (
                          <EyeOff size={14} />
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      
      {watchlistPlayers.length > 0 && (
        <div className="card-footer text-muted text-center">
          <small>
            {language === 'fr'
              ? `${watchlistPlayers.length} joueur${watchlistPlayers.length > 1 ? 's' : ''} surveillé${watchlistPlayers.length > 1 ? 's' : ''}`
              : `${watchlistPlayers.length} player${watchlistPlayers.length > 1 ? 's' : ''} watched`}
          </small>
        </div>
      )}
    </div>
  );
}